/**
 * **Die Bewegungen der Fläche am Zeiger** beim Ziehen von „Neue Aufgabe“ (#301): Entstehen aus dem
 * Button, Wegfallen beim Abbrechen und das Gleiten der echten Eingabe von der Fläche an ihren Platz.
 *
 * Die Werte stammen aus dem Prototyp `tools/new-task-drag-prototype` (Sven, 10.10.2026, Variante A
 * „Wie Verschieben heute“). Größe, Schatten und Neigung sind die von `.drag-overlay-lift`.
 *
 * Bewusst ohne DOM-Bezug: Hier wird gerechnet, eingesetzt wird am Element (`useNewTaskDrag`).
 */

type Ease = readonly [number, number, number, number]

export const NEW_TASK_DRAG = {
  /** Schmal statt zeilenbreit: Sie soll wie etwas aussehen, das man trägt, nicht wie eine Zeile. */
  ghostWidth: 240,
  /**
   * Höhe von Fläche und Lücke: die einer gewöhnlichen zugeklappten Zeile (gemessen 32px). Fest statt
   * an einer Zeile gemessen — trug die erste eine Belohnung oder Marken, wurden beide zu hoch
   * (gemeldet 10.10.2026, Kurz notiert).
   */
  rowHeight: 32,
  /**
   * Höhe von Fläche und Lücke beim Abschnitts-Zug (#314): die eines Abschnittskopfs (`SectionHeader.scss`:
   * 2px Rand oben und unten um die 28px hohe Zeile). Fest aus demselben Grund wie die Zeile.
   */
  sectionHeight: 32,
  scale: 1.02,
  /** Neigung in Grad, Richtung zufällig. */
  tilt: 1.5,
  /** Entstehen aus dem Button. */
  birth: 200,
  /** Gleiten der Eingabe von der Fläche an ihren Platz. */
  glide: 350,
  /** Wegfallen beim Abbrechen. */
  fall: 300,
  fallDistance: 40,
  /** Die Kurve des Hauses (Höhe, Gleiten), wie die Eingabe beim Aufklappen. */
  ease: [0.4, 0, 0.2, 1] as Ease,
  fallEase: [0.42, 0, 1, 1] as Ease,
} as const

export const cssEase = (ease: Ease) => `cubic-bezier(${ease.join(', ')})`

/** Der Schatten der Fläche — der von `.drag-overlay-lift` (ProjectPage.scss). */
export const GHOST_SHADOW = '0 12px 28px rgba(0,0,0,0.12), 0 4px 10px rgba(0,0,0,0.08)'

/** Platz für den Schatten der Karte am Ende des Gleitens. */
const LANDING_SHADOW_ROOM = 12

interface Box {
  left: number
  top: number
  width: number
  height: number
}

/** Wie die Fläche am Zeiger hängt: leicht vergrößert und geneigt. */
export function ghostRestTransform(tilt: number): string {
  return `scale(${NEW_TASK_DRAG.scale}) rotate(${tilt}deg)`
}

/** Links oben der Fläche, wenn der Zeiger HIER steht — gegriffen in der Mitte. */
export function ghostBoxAt(pointer: { x: number; y: number }, size: { width: number; height: number }): Box {
  return { left: pointer.x - size.width / 2, top: pointer.y - size.height / 2, width: size.width, height: size.height }
}

export interface GhostAnimation {
  keyframes: Keyframe[]
  options: KeyframeAnimationOptions
}

/**
 * **Entstehen aus dem Button:** Die Fläche beginnt in Lage und Größe des Buttons und wächst in
 * 200ms an den Zeiger. `null` bei reduzierter Bewegung — dann steht sie sofort da.
 */
export function ghostBirthAnimation(button: Box, ghost: Box, tilt: number, reduced: boolean): GhostAnimation | null {
  if (reduced || ghost.width === 0 || ghost.height === 0) return null
  const dx = button.left - ghost.left
  const dy = button.top - ghost.top
  return {
    keyframes: [
      { transform: `translate(${dx}px, ${dy}px) scale(${button.width / ghost.width}, ${button.height / ghost.height})`, transformOrigin: '0 0', opacity: 0.4, boxShadow: 'none' },
      { transform: ghostRestTransform(tilt), transformOrigin: '50% 50%', opacity: 1, boxShadow: GHOST_SHADOW },
    ],
    options: { duration: NEW_TASK_DRAG.birth, easing: cssEase(NEW_TASK_DRAG.ease), fill: 'forwards' },
  }
}

/**
 * **Abbrechen — sie fällt weg:** 40px nach unten, etwas kleiner, kippt auf die dreifache Neigung und
 * blendet aus. `null` bei reduzierter Bewegung.
 */
export function ghostFallAnimation(tilt: number, reduced: boolean): GhostAnimation | null {
  if (reduced) return null
  return {
    keyframes: [
      { transform: ghostRestTransform(tilt), opacity: 1 },
      { transform: `translateY(${NEW_TASK_DRAG.fallDistance}px) scale(${NEW_TASK_DRAG.scale * 0.95}) rotate(${tilt * 3}deg)`, opacity: 0 },
    ],
    options: { duration: NEW_TASK_DRAG.fall, easing: cssEase(NEW_TASK_DRAG.fallEase), fill: 'forwards' },
  }
}

/**
 * **Die Eingabe gleitet von der Fläche an ihren Platz** (FLIP, wie `dropPopMotion`). Die Eingabe
 * steht schon an ihrem Platz und läuft von der Lage der Fläche dorthin. Sichtbar ist zuerst nur ein
 * Ausschnitt in der Größe der Fläche, links oben — Haken und Text, wie die Fläche (`clip-path`); er
 * wächst zur ganzen Eingabe. Gedreht wird um die Mitte der Fläche.
 *
 * `null` bei reduzierter Bewegung oder ohne Maße (versteckter Tab, jsdom).
 */
export function landingAnimation(from: Box, to: Box, tilt: number, reduced: boolean): GhostAnimation | null {
  if (reduced || to.width === 0 || from.width === 0) return null
  const dx = from.left - to.left
  const dy = from.top - to.top
  const right = Math.max(0, to.width - from.width)
  const bottom = Math.max(0, to.height - from.height)
  const radius = from.height / 2
  return {
    keyframes: [
      {
        transform: `translate(${dx}px, ${dy}px) rotate(${tilt}deg) scale(${NEW_TASK_DRAG.scale})`,
        transformOrigin: `${from.width / 2}px ${from.height / 2}px`,
        clipPath: `inset(0px ${right}px ${bottom}px 0px round ${radius}px)`,
      },
      {
        transform: 'none',
        transformOrigin: `${from.width / 2}px ${from.height / 2}px`,
        // Am Ende über die Kanten hinaus: Der Schatten der Karte liegt außerhalb und soll nicht springen.
        clipPath: `inset(-${LANDING_SHADOW_ROOM}px -${LANDING_SHADOW_ROOM}px -${LANDING_SHADOW_ROOM}px -${LANDING_SHADOW_ROOM}px round ${radius}px)`,
      },
    ],
    options: { duration: NEW_TASK_DRAG.glide, easing: cssEase(NEW_TASK_DRAG.ease) },
  }
}

/**
 * **Die Liste rückt nach:** Die Eingabe steht beim Landen höher da als die Lücke. Ein negativer Rand
 * unten hält den Rest der Liste zuerst dort, wo die Lücke endete, und gibt ihn im Takt des Aufklappens
 * (250ms, wie der Rumpf der Eingabe) frei. `null`, wenn nichts nachzurücken ist.
 */
export function landingMakeRoomAnimation(gapHeight: number, landedHeight: number, reduced: boolean): GhostAnimation | null {
  const grow = landedHeight - gapHeight
  if (reduced || gapHeight === 0 || grow <= 0) return null
  return {
    keyframes: [{ marginBottom: `${-grow}px` }, { marginBottom: '0px' }],
    options: { duration: 250, easing: cssEase(NEW_TASK_DRAG.ease) },
  }
}
